import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import EventImageCarousel from './EventImageCarousel';

interface EventImage {
  id: string;
  image_url: string;
  caption: string | null;
  display_order: number;
}

type EventImagesSectionVariant = 'home' | 'business'

const EventImagesSection: React.FC<{ variant?: EventImagesSectionVariant }> = ({
  variant = 'home'
}) => {
  const [images, setImages] = useState<EventImage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEventImages();
  }, []);

  const fetchEventImages = async () => {
    try {
      const { data, error } = await supabase
        .from('event_images')
        .select('id, image_url, caption, display_order')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setImages(data || []);
    } catch (err) {
      console.error('Error fetching event images:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    if (variant === 'business') {
      return (
        <div className="mx-auto max-w-6xl border border-slate-800 bg-slate-900/35 p-8 md:p-12">
          <div className="animate-pulse">
            <div className="mx-auto mb-6 h-10 w-1/2 rounded bg-white/20" />
            <div className="mx-auto mb-12 h-6 w-2/3 rounded bg-white/20" />
            <div className="aspect-video rounded-none bg-gray-900/50" />
          </div>
        </div>
      )
    }

    return (
      <section className="py-16 md:py-20 bg-slate-800/50 border-t border-slate-800">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="bg-slate-900/35 border border-slate-800 overflow-hidden animate-pulse">
            <div className="relative">
              <div className="absolute top-6 left-6 right-6 z-10">
                <div className="h-8 bg-slate-700 rounded w-64 mb-4" />
                <div className="h-4 bg-slate-700 rounded w-3/4" />
              </div>
              <div className="aspect-video bg-slate-700/40 border-t border-slate-700" />
            </div>
          </div>
        </div>
      </section>
    )
  }

  if (images.length === 0) {
    return null;
  }

  if (variant === 'business') {
    return (
      <div className="mx-auto max-w-6xl border border-slate-800 bg-slate-900/35 p-8 md:p-12">
        <div className="mb-10 text-center md:mb-12">
          <h2 className="mb-4 text-3xl font-bold leading-tight text-white md:text-4xl">
            Tidigare evenemang
          </h2>
          <p className="mx-auto max-w-3xl text-base leading-relaxed text-gray-300 md:text-xl">
            Se bilder från våra tidigare uppdrag och låt dig inspireras
          </p>
        </div>
        <div className="overflow-hidden">
          <EventImageCarousel images={images} autoplayInterval={5000} />
        </div>
      </div>
    )
  }

  return (
    <section className="py-16 md:py-20 border-t border-slate-800">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="bg-slate-900/35 border border-slate-800 overflow-hidden">
          <div className="relative">
            <div className="absolute top-6 left-6 z-10 max-w-md px-4 sm:px-0">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Tidigare evenemang
              </h2>
              <p className="text-slate-400">
                Se bilder från våra tidigare uppdrag och låt dig inspireras
              </p>
            </div>

            <div className="p-4 md:p-6 pt-24 md:pt-24">
              <div className="border border-slate-800 bg-slate-950/10 p-2">
                <div className="overflow-hidden">
                  <EventImageCarousel images={images} autoplayInterval={5000} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
};

export default EventImagesSection;
